import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useMeterReaderList, useUpdateMeterReader } from "../../hooks";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { EyeIcon, PenBox, Search } from "lucide-react";
import { Switch } from "@shared/ui/switch";
import { Button } from "@shared/ui/button";
import EditMeterReader from "./EditMeterReader";
import MeterReaderDetail from "./MeterReaderDetail";
import { logEvent } from "@shared/analytics/analytics";

const MeterReaderManagement = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [details, setDetails] = useState();

  const navigate = useNavigate();
  const currentPage = Number(searchParams.get("page") || "1");
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");

  const { data: meterReaders, isLoading } = useMeterReaderList({
    remoteUtilityId,
    page: currentPage,
    search: searchQuery,
  });

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...({ search: searchTerm }),
    };
    setSearchParams(newParams);
  };

  const { mutate: updateReader } = useUpdateMeterReader();

  const toggleActive = (row: any) => {
    const { id, isActive } = row;
    updateReader({
      id,
      payload: {
        is_active: !isActive,
      },
    }, {
      onSuccess: () => {
        logEvent('MX Meter Reader Toggled', {
          readerId: id,
          is_active: !isActive,
        })
      }
    });
  };

  const columns: TableColumn<any>[] = [
    { key: "employeeId", header: "Emp ID" },
    { key: "name", header: "Name" },
    { key: "contactNumber", header: "Contact" },
    { key: "imeiNo", header: "IMEI No" },
    { key: "createdDate", header: "Created On" },
    { key: "createdUserRemoteName", header: "Created By" },
    {
      key: "isActive",
      header: "Active",
      render: (value, row) => (
        <Switch
          checked={row.isActive}
          onCheckedChange={() => {
            toggleActive(row);
          }}
        />
      ),
    },
  ];

  const actions = [
    {
      label: "View",
      icon: <EyeIcon className="h-5 w-5 text-muted-foreground"/>,
      onClick: (row: any) => {
       setDetails(row);
       setIsDetailOpen(true);
       logEvent("MX Reader Viewed", {
        readerId: row.id
       })
      },
    },
    {
      label: "Edit",
      icon: <PenBox  className="h-5 w-5 text-muted-foreground" />,
      onClick: (row: any) => {
        setIsEditOpen(true);
        setDetails(row);
      },
    },
  ];
  const pagination = meterReaders
    ? {
        currentPage,
        totalPages: Math.ceil(meterReaders.count / 10),
        totalItems: meterReaders.count,
        startItem: (currentPage - 1) * 10 + 1,
        endItem: Math.min(currentPage * 10, meterReaders.count),
        itemsPerPage: 10,
      }
    : undefined;

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <div>
          <p className="text-xl font-bold">Meter Reader Management</p>
          <p className="text-sm font-normal text-neutral-600 pt-1">
            Manage your meter readers
          </p>
        </div>
        <Button className="px-6" onClick={() => navigate('/utility/utility-setup/user-management')}>Add Meter Reader</Button>
      </div>
      <GenericTable
        data={meterReaders?.results}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        pagination={pagination}
        actions={actions}
        actionsType="icons"
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        onSearchValueChange={handleSearch}
        searchPlaceholder="Search by name, employee-id..."
        searchDebounceMs={500}
        emptyMessage="No meter readers found"
        emptyIcon={<Search className="h-8 w-8 text-muted-foreground mb-2" />}
      />
      <EditMeterReader open={isEditOpen} onOpenChange={setIsEditOpen} details={details} />
      <MeterReaderDetail open={isDetailOpen} onOpenChange={setIsDetailOpen} detail={details} />
    </div>
  );
};

export default MeterReaderManagement;
